import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import {
  ciRuleAutoQuery,
  ciRulesQuery,
  removeCiRule,
  removeCiSubscriber,
  saveCiNotifyOwners,
  removeCiThreshold,
  saveCiAlerts,
  saveCiAutoRule,
  saveCiRule,
  saveCiSubscriber,
  saveCiThreshold,
  type CiRule,
  type CiRulesPage as PageData,
  type CiSubscriber,
  type CiThreshold,
  type WriteResult,
} from '../../api/ciRules'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { FormRow, HalfField } from '../../ui/FormGrid'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './CiRulesPage.css'

/**
 * Settings -> CI rules, mutat din
 * `views/version-3/settings/ci_rules/{index,rule_form,auto_form,threshold_form,
 * subscriber_form}.php`.
 *
 * Adresa e cea din portal - `/app/settings/ci_rules` - fiindca aia e scrisa in
 * `Services\Navigation\SettingsMenu` (zona Policies), si dupa ea se aprinde
 * randul din rail.
 *
 * -----------------------------------------------------------------------------
 * CE HOTARASTE OMUL AICI
 * -----------------------------------------------------------------------------
 *
 *  1. VOCABULARUL: ce fel de legatura are voie sa existe intre ce fel de
 *     elemente. E singurul loc unde se scrie, si tot el e cel dupa care
 *     sanatatea CMDB numara muchiile pe care macheta nu le permite.
 *  2. AUTOMATUL: aceeasi regula poate purta conditii dupa care platforma face
 *     singura legatura. Fereastra e SEPARATA, si nu din mofturi: gresind
 *     vocabularul interzici o muchie pe care o voia cineva, gresind automatul
 *     faci mii pe care nu le voia nimeni.
 *  3. PRAGURILE: cat de sigura sa fie descoperirea inainte sa spuna cuiva.
 *  4. ALARMELE: cui ii spune. Un prag fara auditoriu e chiar starea in care a
 *     stat modulul asta - schimbari scrise intr-o tabela pe care n-o citea
 *     nimic.
 *
 * -----------------------------------------------------------------------------
 * TABELE SIMPLE, NU `ui/DataTable`
 * -----------------------------------------------------------------------------
 *
 * Sunt TREI liste una sub alta pe acelasi ecran, iar `DataTable` isi pune
 * cautarea si paginarea in antetul PAGINII (`claimHeaderTools`) - trei tabele
 * s-ar fi batut pe acelasi loc. Portalul insusi le deseneaza aici ca tabele
 * obisnuite; clasele sunt ale lui, si nu unele noi.
 *
 * DREPTURILE SE VAD PE ECRAN, DAR SE HOTARASC PE SERVER
 *
 * `canWrite` / `canEditThresholds` doar nu deseneaza butoane. Cine trimite
 * cererea de mana primeste 403 din `App\Settings\CiRules`, cu aceleasi chei ca
 * in portal.
 */
/*
 * Pastila gravitatii unei alarme.
 *
 * Inainte era `style={{ background: alarm.color }}` - culoarea pe care o
 * trimite serverul (#1ba1e2 pentru Info, #e6ac23 pentru Major) - sub cerneala
 * consolei. Masurat in Edge cu foile de productie: "Info" iesea la 2,02:1 si
 * "Major" la 2,87:1, amandoua sub pragul de 4,5:1.
 *
 * Clasele consolei duc perechea gandita pentru pastile - tenta palida si scris
 * inchis din aceeasi familie - si o duc la fel peste toata zona. Numele
 * gravitatii vine tot de la server, deci felul se alege dupa el, nu dupa un
 * numar care ar putea sa insemne altceva maine.
 */
const FLAG_CLASS: Record<string, string> = {
  info: 'label-info',
  minor: 'label-success',
  major: 'label-warning',
  critical: 'label-danger',
}

function flagClass(flagName: string): string {
  return FLAG_CLASS[flagName.trim().toLowerCase()] ?? 'label-default'
}

export function CiRulesPage() {
  const [onlyAuto, setOnlyAuto] = useState(false)
  const page = useQuery(ciRulesQuery(onlyAuto))
  const client = useQueryClient()

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'ci_rules'] })

  /** Fereastra deschisa acum: felul ei si randul pe care il schimba. */
  const [ruleForm, setRuleForm] = useState<CiRule | null | undefined>(undefined)
  const [autoId, setAutoId] = useState(0)
  const [thresholdForm, setThresholdForm] = useState<CiThreshold | null | undefined>(undefined)
  const [subscriberForm, setSubscriberForm] = useState<CiSubscriber | null | undefined>(undefined)

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data

  const kindLabel = (value: number | null) =>
    data.kinds.find((kind) => kind.value === value)?.label ?? '?'

  const drop = (question: string, run: () => Promise<WriteResult>) => {
    if (!window.confirm(question)) return
    void run()
      .then((result) => {
        toast(result.message, 'CI rules', result.status === 'OK' ? 'success' : 'error')
        if (result.status === 'OK') reload()
      })
      .catch(() => toast('That could not be saved.', 'CI rules', 'error'))
  }

  return (
    <div id="content">
      <header className="panel-heading">
        {data.canWrite && (
          <button className="pull-right btn btn-success btn-sm" id="ruleAdd" type="button" onClick={() => setRuleForm(null)}>
            <i className="fas fa-plus-circle"></i> Add a rule
          </button>
        )}
        <h4>
          <strong>Relation</strong> rules
        </h4>
        <label className="color">which relations are allowed, and which ones build themselves</label>
      </header>

      <div className="row">
        <div className="col-md-12">
          <p>
            {/*
             * Lista e de 88 de randuri pe o instalare proaspata, si cele cateva
             * care poarta o regula automata sunt ce cauta de obicei omul.
             */}
            <button className="btn btn-default btn-sm" id="ruleFilter" type="button" onClick={() => setOnlyAuto(!onlyAuto)}>
              {onlyAuto ? 'Show all rules' : 'Only automatic ones'}
            </button>
          </p>

          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>From</th>
                <th>Relation</th>
                <th>To</th>
                <th className="text-center">How many</th>
                <th>Automatic</th>
                <th className="text-center">Active</th>
                <th className="text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {data.rules.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <span className="label label-default">No rules.</span>
                  </td>
                </tr>
              ) : (
                data.rules.map((rule) => (
                  <tr key={rule.ID}>
                    <td>{rule.source_class_name !== '' ? rule.source_class_name : <em>{kindLabel(rule.source_kind)}</em>}</td>
                    <td>{rule.relation_name}</td>
                    <td>{rule.target_class_name !== '' ? rule.target_class_name : <em>{kindLabel(rule.target_kind)}</em>}</td>
                    <td className="text-center text-muted">
                      {rule.min_cardinality} &ndash; {rule.max_cardinality === null ? <>&infin;</> : rule.max_cardinality}
                    </td>
                    <td>
                      {rule.auto_conditions === 0 ? (
                        <span className="label label-default">no</span>
                      ) : (
                        <span className="label label-success">{rule.auto_conditions} condition(s)</span>
                      )}
                    </td>
                    <td className="text-center">
                      {rule.status === 1 ? (
                        <span className="label label-success">yes</span>
                      ) : (
                        <span className="label label-default">no</span>
                      )}
                    </td>
                    <td className="text-right">
                      {data.canWrite && (
                        <>
                          <button
                            className="btn btn-default btn-xs autoEdit"
                            type="button"
                            title="Automatic matching"
                            onClick={() => setAutoId(rule.ID)}
                          >
                            <i className="fa fa-magic"></i>
                          </button>{' '}
                          <button className="btn btn-default btn-xs ruleEdit" type="button" onClick={() => setRuleForm(rule)}>
                            <i className="fa fa-pencil-alt"></i>
                          </button>{' '}
                        </>
                      )}
                      {data.canDelete && (
                        <button
                          className="btn btn-danger btn-xs ruleRemove"
                          type="button"
                          onClick={() =>
                            drop('Remove this rule? Relations it already created are left in place.', () => removeCiRule(rule.ID))
                          }
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data.canThresholds && (
        <>
          <div className="row">
            <div className="col-md-12">
              <header className="panel-heading">
                {data.canEditThresholds && (
                  <button
                    className="pull-right btn btn-success btn-sm"
                    id="thresholdAdd"
                    type="button"
                    onClick={() => setThresholdForm(null)}
                  >
                    <i className="fas fa-plus-circle"></i> Add a threshold
                  </button>
                )}
                <h4>
                  <strong>Change</strong> thresholds
                </h4>
                <label className="color">how sure the platform has to be before anybody is told</label>
              </header>

              <p className="text-muted">
                How often a connection has to be seen, and how confidently, before a new dependency is announced. Without a
                threshold every first sighting becomes an alert &mdash; and the first thing anybody does with a noisy alert is
                switch it off.
                <br />
                Where nothing matches, the built-in floor is <strong>{data.defaults.observations} observations</strong> at{' '}
                <strong>{data.defaults.confidence}%</strong>. A relation is announced as disappeared after{' '}
                <strong>{data.defaults.staleMinutes} minutes</strong> without traffic, unless a threshold says otherwise.
              </p>

              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Applies to</th>
                    <th>Customer</th>
                    <th className="text-center">Observations</th>
                    <th className="text-center">Confidence</th>
                    <th className="text-center">Surge alert</th>
                    <th className="text-center">Disappeared after</th>
                    <th className="text-center">Active</th>
                    <th className="text-right">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {data.thresholds.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <span className="label label-default">Nothing configured.</span> The built-in floor applies everywhere.
                      </td>
                    </tr>
                  ) : (
                    data.thresholds.map((threshold) => (
                      <tr key={threshold.ID}>
                        <td>
                          {threshold.scope_label}
                          {threshold.scope_name !== '' ? ` — ${threshold.scope_name}` : ''}
                        </td>
                        <td className="text-muted">{threshold.company_name !== '' ? threshold.company_name : 'All'}</td>
                        <td className="text-center">{threshold.min_observations}</td>
                        <td className="text-center">{threshold.min_confidence}%</td>
                        <td className="text-center">
                          {threshold.new_edges_per_hour_alert === null ? (
                            <span className="text-muted">off</span>
                          ) : (
                            `${threshold.new_edges_per_hour_alert}/h`
                          )}
                        </td>
                        <td className="text-center staleMinutes">
                          {threshold.stale_minutes === null ? (
                            <span className="text-muted">{data.defaults.staleMinutes} min (default)</span>
                          ) : (
                            `${threshold.stale_minutes} min`
                          )}
                        </td>
                        <td className="text-center">
                          {threshold.status === 1 ? (
                            <span className="label label-success">yes</span>
                          ) : (
                            <span className="label label-default">no</span>
                          )}
                        </td>
                        <td className="text-right">
                          {data.canEditThresholds && (
                            <button
                              className="btn btn-default btn-xs thresholdEdit"
                              type="button"
                              onClick={() => setThresholdForm(threshold)}
                            >
                              <i className="fa fa-pencil-alt"></i>
                            </button>
                          )}{' '}
                          {data.canDeleteThresholds && (
                            <button
                              className="btn btn-danger btn-xs thresholdRemove"
                              type="button"
                              onClick={() =>
                                drop('Remove this threshold? The built-in floor applies again where nothing else matches.', () =>
                                  removeCiThreshold(threshold.ID),
                                )
                              }
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/*
           * Alarmele stau CHIAR SUB praguri: cele doua jumatati ale aceleiasi
           * hotarari erau despartite intre o tabela si nimic - pragul spunea cat
           * de sigur sa fie, si apoi nu era nimeni caruia sa i se spuna.
           */}
          <div className="row">
            <div className="col-md-12">
              <header className="panel-heading">
                {data.canEditThresholds && (
                  <button
                    className="pull-right btn btn-success btn-sm"
                    id="subscriberAdd"
                    type="button"
                    onClick={() => setSubscriberForm(null)}
                  >
                    <i className="fas fa-plus-circle"></i> Add an alarm
                  </button>
                )}
                <h4>
                  <strong>Change</strong> alarms
                </h4>
                <label className="color">
                  who is emailed when the graph changes &mdash; one message an hour listing what moved, not one per change
                </label>
              </header>

              <OwnersSwitch data={data} onSaved={reload} />

              <PendingNote pending={data.pending} />

              <UnauthorizedEmailSwitch on={data.unauthorizedEmail} canEdit={data.canEditThresholds} onSaved={reload} />

              {data.subscribers.length === 0 ? (
                <div className="adm-empty-inline">
                  <i className="fas fa-envelope"></i>
                  {data.notifyOwners
                    ? 'No alarm is set. Only the owners of the items involved are emailed.'
                    : 'No alarm is set. Dependency changes are detected and stored, and no message goes out.'}
                </div>
              ) : (
                <table className="table table-hover adm-list">
                  <thead>
                    <tr>
                      <th>Emails</th>
                      <th>Customer</th>
                      <th>Alarms</th>
                      <th className="adm-numeric">From</th>
                      <th>Last sent</th>
                      <th className="text-right">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.subscribers.map((subscriber) => (
                      <tr key={subscriber.ID} className={subscriber.status === 1 ? undefined : 'adm-muted'}>
                        <td>{subscriber.address}</td>
                        <td>{subscriber.company_name !== '' ? subscriber.company_name : 'Every customer'}</td>
                        <td>
                          {data.alarms
                            .filter((alarm) => Number((subscriber as unknown as Record<string, unknown>)[alarm.key]) === 1)
                            .map((alarm) => (
                              <span key={alarm.key} className={`label ${flagClass(alarm.flag_name)}`} title={alarm.label}>
                                {alarm.flag_name}
                              </span>
                            ))}
                        </td>
                        <td className="adm-numeric">{subscriber.min_changes}</td>
                        <td className="adm-muted adm-nowrap">{subscriber.last_sent_at ?? 'never'}</td>
                        <td className="text-right adm-nowrap">
                          {data.canEditThresholds && (
                            <>
                              <button
                                className="btn btn-default btn-xs subscriberEdit"
                                type="button"
                                onClick={() => setSubscriberForm(subscriber)}
                              >
                                <i className="fas fa-pen"></i>
                              </button>{' '}
                              <button
                                className="btn btn-danger btn-xs subscriberRemove"
                                type="button"
                                onClick={() => drop('Remove this alarm?', () => removeCiSubscriber(subscriber.ID))}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {ruleForm !== undefined && (
        <RuleModal data={data} rule={ruleForm} onClose={() => setRuleForm(undefined)} onSaved={reload} />
      )}
      {autoId > 0 && <AutoModal id={autoId} onClose={() => setAutoId(0)} onSaved={reload} />}
      {thresholdForm !== undefined && (
        <ThresholdModal data={data} threshold={thresholdForm} onClose={() => setThresholdForm(undefined)} onSaved={reload} />
      )}
      {subscriberForm !== undefined && (
        <SubscriberModal data={data} subscriber={subscriberForm} onClose={() => setSubscriberForm(undefined)} onSaved={reload} />
      )}
    </div>
  )
}

/**
 * "Notify owners" - cerinta 97.
 *
 * Aprins, responsabilii (business si tehnic) ai elementelor unei legaturi si ai
 * serviciilor care depind de ele primesc schimbarile, fara sa fie scrisi de
 * mana mai jos. O adresa care e si abonata primeste UN mesaj, nu doua.
 */
function OwnersSwitch({ data, onSaved }: { data: PageData; onSaved: () => void }) {
  const [saving, setSaving] = useState(false)

  const toggle = (on: boolean) => {
    setSaving(true)
    void saveCiNotifyOwners(on)
      .then((result) => {
        toast(result.message, 'CI rules', result.status === 'OK' ? 'success' : 'error')
        if (result.status === 'OK') onSaved()
      })
      .catch(() => toast('That could not be saved.', 'CI rules', 'error'))
      .finally(() => setSaving(false))
  }

  return (
    <div className="checkbox adm-owners-switch">
      <label>
        <input
          type="checkbox"
          name="notify_owners"
          checked={data.notifyOwners}
          disabled={!data.canNotifyOwners || saving}
          onChange={(event) => toggle(event.target.checked)}
        />{' '}
        <strong>Notify owners</strong>
        <span className="adm-muted">
          {' '}
          &mdash; the business and technical owners of the items on both ends of a connection, and of the services
          that depend on them, are emailed when their connections appear or stop. One message per address, together
          with the alarms below.
        </span>
      </label>
    </div>
  )
}

/**
 * Cate schimbari asteapta, si daca are cine sa le primeasca.
 *
 * A doua cifra e cea care conteaza: schimbari care nu potrivesc scopul niciunui
 * abonat stau netrimise pentru totdeauna, iar un ecran care nu spune asta arata
 * ca unul pe care nu se intampla nimic.
 */
/**
 * Cerinta 86: mailul cu schimbarile neautorizate pe echipamente - catre
 * proprietarul si responsabilul tehnic al CI-ului si catre adresele de mai jos.
 * Unul pe furnizor, implicit pornit; il trimite cronul ph4 la fiecare trecere.
 */
function UnauthorizedEmailSwitch({ on, canEdit, onSaved }: { on: boolean; canEdit: boolean; onSaved(): void }) {
  const [busy, setBusy] = useState(false)

  const flip = (next: boolean) => {
    setBusy(true)
    void saveCiAlerts(next)
      .then((result) => {
        toast(result.message, 'CI rules', result.status === 'OK' ? 'success' : 'error')
        if (result.status === 'OK') onSaved()
      })
      .catch(() => toast('That could not be saved.', 'CI rules', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="checkbox" id="unauthorizedEmail">
      <label>
        <input
          type="checkbox"
          checked={on}
          disabled={!canEdit || busy}
          onChange={(event) => flip(event.target.checked)}
        />{' '}
        Email <strong>unauthorized changes</strong> on equipment to the item&apos;s owners and to the addresses below
        <span className="adm-muted">
          {' '}
          &mdash; a change is authorized by a maintenance window or by an approved change request that lists the item
        </span>
      </label>
    </div>
  )
}

function PendingNote({ pending }: { pending: { waiting: number; subscribers: number } }) {
  if (pending.waiting === 0) return null

  const many = pending.waiting === 1 ? '' : 's'

  if (pending.subscribers === 0) {
    return (
      <div className="alert alert-warning" style={{ margin: '12px 0' }}>
        <i className="fas fa-exclamation-circle"></i> <strong>{pending.waiting.toLocaleString('en-US')}</strong> dependency
        change{many} recorded and no alarm set to send them. They are kept, not discarded &mdash; the first alarm added receives
        them.
      </div>
    )
  }

  return (
    <div className="alert alert-info" style={{ margin: '12px 0' }}>
      <i className="fas fa-clock"></i> <strong>{pending.waiting.toLocaleString('en-US')}</strong> change{many} waiting for the
      next pass, which runs at half past the hour.
    </div>
  )
}

/**
 * Jumatatea de vocabular a unei reguli.
 *
 * Fiecare parte e ori o clasa, ori un FEL de element. Amandoua deodata nu se
 * ofera: motorul prefera clasa, deci felul ar sta acolo fara sa faca nimic si
 * s-ar citi ca si cum ar conta.
 */
function RuleModal({
  data,
  rule,
  onClose,
  onSaved,
}: {
  data: PageData
  rule: CiRule | null
  onClose(): void
  onSaved(): void
}) {
  const [busy, setBusy] = useState(false)
  const [type, setType] = useState(rule?.relation_type_id ?? data.types[0]?.id ?? 0)
  const [sourceKind, setSourceKind] = useState(rule?.source_kind ?? 0)
  const [sourceClass, setSourceClass] = useState(rule?.source_class_id ?? 0)
  const [targetKind, setTargetKind] = useState(rule?.target_kind ?? 0)
  const [targetClass, setTargetClass] = useState(rule?.target_class_id ?? 0)
  const [min, setMin] = useState(String(rule?.min_cardinality ?? 0))
  const [max, setMax] = useState(rule?.max_cardinality === null || rule === null ? '' : String(rule.max_cardinality))
  const [status, setStatus] = useState(rule === null ? 1 : rule.status)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveCiRule({
      ID: rule?.ID ?? 0,
      relation_type_id: type,
      source_class_id: sourceClass,
      target_class_id: targetClass,
      source_kind: sourceKind,
      target_kind: targetKind,
      min_cardinality: min === '' ? 0 : Number(min),
      max_cardinality: max,
      status,
    })
      .then((result) => {
        setBusy(false)
        toast(result.message, 'CI rules', result.status === 'OK' ? 'success' : 'error')
        if (result.status !== 'OK') return
        onSaved()
        onClose()
      })
      .catch(() => {
        setBusy(false)
        toast('That could not be saved.', 'CI rules', 'error')
      })
  }

  return (
    <Modal
      id="ciRulesModal"
      open
      large
      onClose={onClose}
      title={rule === null ? 'Add a relation rule' : 'Edit relation rule'}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="ciRulesForm" className="btn btn-primary" disabled={busy}>
            Save
          </button>
        </>
      }
    >
      <form id="ciRulesForm" onSubmit={submit}>
        <FormRow>
          <HalfField label="Relation" htmlFor="ruleRelationType" span={12}>
            <select
              className="form-control"
              id="ruleRelationType"
              value={type}
              onChange={(event) => setType(Number(event.target.value))}
            >
              {data.types.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name} ({item.code})
                </option>
              ))}
            </select>
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="From — any item of type" htmlFor="sourceKind">
            <KindSelect id="sourceKind" value={sourceKind} pairs={data.kinds} onChange={setSourceKind} />
          </HalfField>
          <HalfField label="To — any item of type" htmlFor="targetKind">
            <KindSelect id="targetKind" value={targetKind} pairs={data.kinds} onChange={setTargetKind} />
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="…or one specific class" htmlFor="sourceClass">
            <ClassSelect id="sourceClass" value={sourceClass} classes={data.classes} onChange={setSourceClass} />
          </HalfField>
          <HalfField label="…or one specific class" htmlFor="targetClass">
            <ClassSelect id="targetClass" value={targetClass} classes={data.classes} onChange={setTargetClass} />
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="At least" htmlFor="minCard" span={3}>
            <input
              type="number"
              className="form-control"
              id="minCard"
              min={0}
              value={min}
              onChange={(event) => setMin(event.target.value)}
            />
          </HalfField>
          <HalfField
            label="At most"
            htmlFor="maxCard"
            span={3}
            /* Asta E ce face dintr-o legatura o componenta: un `contains` cu
               plafon, nu alt mecanism. */
            help="Also what makes a relation a component."
          >
            <input
              type="number"
              className="form-control"
              id="maxCard"
              min={1}
              placeholder="no limit"
              value={max}
              onChange={(event) => setMax(event.target.value)}
            />
          </HalfField>
          <HalfField label="Active" htmlFor="ruleStatus" span={6}>
            <select
              className="form-control"
              id="ruleStatus"
              value={status}
              onChange={(event) => setStatus(Number(event.target.value))}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </HalfField>
        </FormRow>
      </form>
    </Modal>
  )
}

function KindSelect({
  id,
  value,
  pairs,
  onChange,
}: {
  id: string
  value: number
  pairs: { value: number; label: string }[]
  onChange(value: number): void
}) {
  return (
    <select className="form-control" id={id} value={value} onChange={(event) => onChange(Number(event.target.value))}>
      <option value={0}>&mdash;</option>
      {pairs.map((pair) => (
        <option value={pair.value} key={pair.value}>
          {pair.label}
        </option>
      ))}
    </select>
  )
}

function ClassSelect({
  id,
  value,
  classes,
  onChange,
}: {
  id: string
  value: number
  classes: { id: number; name: string }[]
  onChange(value: number): void
}) {
  return (
    <select className="form-control" id={id} value={value} onChange={(event) => onChange(Number(event.target.value))}>
      <option value={0}>&mdash;</option>
      {classes.map((item) => (
        <option value={item.id} key={item.id}>
          {item.name}
        </option>
      ))}
    </select>
  )
}

/**
 * Jumatatea automata: conditiile dupa care platforma leaga singura doua
 * elemente.
 *
 * Cheile de proprietate se ALEG dintr-o lista, nu se scriu: o cheie care nu
 * exista nu potriveste nimic, tacut, la trei dimineata.
 */
function AutoModal({ id, onClose, onSaved }: { id: number; onClose(): void; onSaved(): void }) {
  const form = useQuery(ciRuleAutoQuery(id))
  const [rows, setRows] = useState<{ source: string; target: string }[] | null>(null)
  const [confidence, setConfidence] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const conditions = rows ?? (form.data ? (form.data.conditions.length > 0 ? form.data.conditions : [{ source: '', target: '' }]) : [])
  const sure = confidence ?? String(form.data?.confidence ?? 100)

  const set = (index: number, side: 'source' | 'target', value: string) => {
    const next = conditions.map((row, i) => (i === index ? { ...row, [side]: value } : row))
    setRows(next)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveCiAutoRule(id, conditions, Number(sure) || 100)
      .then((result) => {
        setBusy(false)
        toast(result.message, 'CI rules', result.status === 'OK' ? 'success' : 'error')
        if (result.status !== 'OK') return
        onSaved()
        onClose()
      })
      .catch(() => {
        setBusy(false)
        toast('That could not be saved.', 'CI rules', 'error')
      })
  }

  return (
    <Modal
      id="ciRulesModal"
      open
      large
      onClose={onClose}
      title="Automatic matching"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="ciRulesForm" className="btn btn-primary" disabled={busy || !form.data}>
            Save
          </button>
        </>
      }
    >
      {!form.data ? (
        <Spinner />
      ) : (
        <form id="ciRulesForm" onSubmit={submit}>
          <p className="text-muted">
            The platform links two items whenever every condition below holds &mdash; for example, a virtual machine belongs to
            the cluster whose <em>name</em> equals its <em>cluster</em> property.
            <br />
            <br />
            The link is made on its own, without anybody confirming it: the rule is either satisfied or it is not, and there is
            nothing to weigh up. These relations are not guessed from traffic, so the change detector leaves them alone. Leave
            the list empty to switch automatic matching off.
          </p>

          <div id="autoRows">
            {conditions.map((row, index) => (
              <div className="row autoRow" style={{ marginBottom: 8 }} key={index}>
                <div className="col-md-5">
                  <SideSelect name="source" value={row.source} fields={form.data.sourceFields} onChange={(v) => set(index, 'source', v)} />
                </div>
                <div className="col-md-1 text-center" style={{ paddingTop: 6 }}>
                  =
                </div>
                <div className="col-md-5">
                  <SideSelect name="target" value={row.target} fields={form.data.targetFields} onChange={(v) => set(index, 'target', v)} />
                </div>
                <div className="col-md-1">
                  <button
                    type="button"
                    className="btn btn-default btn-sm autoRemove"
                    onClick={() => setRows(conditions.filter((_, i) => i !== index))}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p>
            <button type="button" className="btn btn-default btn-sm" id="autoAdd" onClick={() => setRows([...conditions, { source: '', target: '' }])}>
              <i className="fa fa-plus"></i> Add a condition
            </button>
          </p>

          <hr />

          <FormRow>
            <HalfField
              label="Confidence"
              htmlFor="autoConfidence"
              span={3}
              help="Recorded on every relation the rule creates, for reporting. It does not decide whether the relation is made — the conditions do."
            >
              <input
                type="number"
                className="form-control"
                id="autoConfidence"
                min={1}
                max={100}
                value={sure}
                onChange={(event) => setConfidence(event.target.value)}
              />
            </HalfField>
          </FormRow>

          {form.data.sourceFields.length === 0 && form.data.targetFields.length === 0 && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle"></i> Neither side of this rule names a specific class, so no properties
              can be listed. Matching on the item name still works.
            </div>
          )}
        </form>
      )}
    </Modal>
  )
}

/** O parte a unei conditii, ca lista cu tot ce intelege motorul. */
function SideSelect({
  name,
  value,
  fields,
  onChange,
}: {
  name: string
  value: string
  fields: { key: string; label: string }[]
  onChange(value: string): void
}) {
  /*
   * O valoare deja scrisa care nu e nici numele, nici o proprietate a clasei -
   * un `attr:` sau un `const:` pus de altcineva - se pastreaza ca atare, ca
   * deschiderea ferestrei sa nu o stearga tacut.
   */
  const known = value === '' || value === 'column:name' || fields.some((field) => `field:${field.key}` === value)

  return (
    <select className="form-control input-sm" name={`${name}[]`} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">&mdash;</option>
      <optgroup label="Name">
        <option value="column:name">The item name</option>
      </optgroup>
      {fields.length > 0 && (
        <optgroup label="Property">
          {fields.map((field) => (
            <option value={`field:${field.key}`} key={field.key}>
              {field.label}
            </option>
          ))}
        </optgroup>
      )}
      {!known && <option value={value}>{value}</option>}
    </select>
  )
}

/** Un prag. Cel mai potrivit castiga: un element bate clasa lui, clasa bate tot. */
/* `CiRules::MIN_STALE_MINUTES` / `MAX_STALE_MINUTES`: 15 e podeaua detectorului, 10080 o saptamana. */
const STALE_MIN = 15
const STALE_MAX = 10080

function ThresholdModal({
  data,
  threshold,
  onClose,
  onSaved,
}: {
  data: PageData
  threshold: CiThreshold | null
  onClose(): void
  onSaved(): void
}) {
  const [busy, setBusy] = useState(false)
  const [scope, setScope] = useState(threshold?.scope_type ?? data.scopes[0]?.value ?? 1)
  const [company, setCompany] = useState(threshold?.company_id ?? 0)
  const [scopeId, setScopeId] = useState(String(threshold?.scope_id ?? ''))
  const [observations, setObservations] = useState(String(threshold?.min_observations ?? data.defaults.observations))
  const [confidence, setConfidence] = useState(String(threshold?.min_confidence ?? data.defaults.confidence))
  const [surge, setSurge] = useState(threshold?.new_edges_per_hour_alert === null || threshold === null ? '' : String(threshold.new_edges_per_hour_alert))
  const [staleMinutes, setStaleMinutes] = useState(
    threshold?.stale_minutes === null || threshold === null ? '' : String(threshold.stale_minutes),
  )
  const [status, setStatus] = useState(threshold === null ? 1 : threshold.status)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const stale = staleMinutes.trim()
    if (stale !== '' && (!/^\d+$/.test(stale) || Number(stale) < STALE_MIN || Number(stale) > STALE_MAX)) {
      toast(`Disappeared after is between ${STALE_MIN} and ${STALE_MAX} minutes.`, 'CI rules', 'error')
      return
    }
    setBusy(true)
    void saveCiThreshold({
      ID: threshold?.ID ?? 0,
      company_id: company,
      scope_type: scope,
      scope_id: scopeId === '' ? 0 : Number(scopeId),
      min_observations: observations === '' ? 0 : Number(observations),
      min_confidence: confidence === '' ? 0 : Number(confidence),
      new_edges_per_hour_alert: surge,
      stale_minutes: stale,
      status,
    })
      .then((result) => {
        setBusy(false)
        toast(result.message, 'CI rules', result.status === 'OK' ? 'success' : 'error')
        if (result.status !== 'OK') return
        onSaved()
        onClose()
      })
      .catch(() => {
        setBusy(false)
        toast('That could not be saved.', 'CI rules', 'error')
      })
  }

  return (
    <Modal
      id="ciRulesModal"
      open
      large
      onClose={onClose}
      title={threshold === null ? 'Add a threshold' : 'Edit threshold'}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="ciRulesForm" className="btn btn-primary" disabled={busy}>
            Save
          </button>
        </>
      }
    >
      <form id="ciRulesForm" onSubmit={submit}>
        <FormRow>
          <HalfField
            label="Applies to"
            htmlFor="thresholdScope"
            help="The most specific match wins, so a rule for one item beats one for its class."
          >
            <select
              className="form-control"
              id="thresholdScope"
              value={scope}
              onChange={(event) => setScope(Number(event.target.value))}
            >
              {data.scopes.map((pair) => (
                <option value={pair.value} key={pair.value}>
                  {pair.label}
                </option>
              ))}
            </select>
          </HalfField>
          <HalfField label="Customer" htmlFor="thresholdCompany">
            <select
              className="form-control"
              id="thresholdCompany"
              value={company}
              onChange={(event) => setCompany(Number(event.target.value))}
            >
              <option value={0}>All customers</option>
              {data.companies.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </HalfField>
        </FormRow>

        {scope === 2 && (
          <FormRow>
            <HalfField label="CI class" htmlFor="thresholdClass">
              <select
                className="form-control"
                id="thresholdClass"
                value={scopeId}
                onChange={(event) => setScopeId(event.target.value)}
              >
                <option value="">&mdash;</option>
                {data.classes.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>
        )}

        {scope === 3 && (
          <FormRow>
            <HalfField label="Item id" htmlFor="thresholdCi" help="From the address bar of the item record, /cmdb/view/&lt;id&gt;.">
              <input
                type="number"
                className="form-control"
                id="thresholdCi"
                value={scopeId}
                onChange={(event) => setScopeId(event.target.value)}
              />
            </HalfField>
          </FormRow>
        )}

        <FormRow>
          <HalfField label="Seen at least" htmlFor="minObs" span={3} help="times before it is announced">
            <input
              type="number"
              className="form-control"
              id="minObs"
              min={1}
              value={observations}
              onChange={(event) => setObservations(event.target.value)}
            />
          </HalfField>
          <HalfField label="Confidence at least" htmlFor="minConf" span={3}>
            <input
              type="number"
              className="form-control"
              id="minConf"
              min={1}
              max={100}
              value={confidence}
              onChange={(event) => setConfidence(event.target.value)}
            />
          </HalfField>
          <HalfField
            label="Surge alert"
            htmlFor="surge"
            span={6}
            /* Un val e rareori un succes al descoperirii. De obicei e o subretea
               care tocmai a devenit vizibila, sau un releu indreptat gresit. */
            help="new relations per hour before raising a flag"
          >
            <input
              type="number"
              className="form-control"
              id="surge"
              min={1}
              placeholder="off"
              value={surge}
              onChange={(event) => setSurge(event.target.value)}
            />
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField
            label="Disappeared after (minutes)"
            htmlFor="staleMinutes"
            help={`A relation with no traffic for this many minutes is announced as disappeared. Intermittent links — a nightly backup, an hourly job — need large values. Between ${STALE_MIN} and ${STALE_MAX}; empty means ${data.defaults.staleMinutes} (24 hours).`}
          >
            <input
              type="number"
              className="form-control"
              id="staleMinutes"
              min={STALE_MIN}
              max={STALE_MAX}
              placeholder={String(data.defaults.staleMinutes)}
              value={staleMinutes}
              onChange={(event) => setStaleMinutes(event.target.value)}
            />
          </HalfField>
          <HalfField label="Active" htmlFor="thresholdStatus">
            <select
              className="form-control"
              id="thresholdStatus"
              value={status}
              onChange={(event) => setStatus(Number(event.target.value))}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </HalfField>
        </FormRow>
      </form>
    </Modal>
  )
}

/**
 * O adresa, si ce afla ea.
 *
 * Cele trei feluri sunt comutatoare separate fiindca au auditorii cu adevarat
 * diferiti: o dependenta noua sau una care s-a oprit privesc pe cine intretine
 * serviciul; un val de patruzeci de legaturi intr-o ora e aproape intotdeauna
 * problema platformei, si priveste pe cine tine descoperirea.
 */
function SubscriberModal({
  data,
  subscriber,
  onClose,
  onSaved,
}: {
  data: PageData
  subscriber: CiSubscriber | null
  onClose(): void
  onSaved(): void
}) {
  const [busy, setBusy] = useState(false)
  const [address, setAddress] = useState(subscriber?.address ?? '')
  const [company, setCompany] = useState(subscriber?.company_id ?? 0)
  const [appeared, setAppeared] = useState(subscriber === null ? true : subscriber.on_appeared === 1)
  const [disappeared, setDisappeared] = useState(subscriber === null ? true : subscriber.on_disappeared === 1)
  const [surge, setSurge] = useState(subscriber !== null && subscriber.on_surge === 1)
  const [min, setMin] = useState(String(subscriber?.min_changes ?? 1))

  const flagOf = (key: string) => data.alarms.find((alarm) => alarm.key === key)

  const badge = (key: string) => {
    const alarm = flagOf(key)
    if (!alarm) return null
    return (
      <span className={`label ${flagClass(alarm.flag_name)}`} style={{ verticalAlign: 1 }}>
        {alarm.flag_name}
      </span>
    )
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveCiSubscriber({
      ID: subscriber?.ID ?? 0,
      address,
      company_id: company,
      on_appeared: appeared ? 1 : 0,
      on_disappeared: disappeared ? 1 : 0,
      on_surge: surge ? 1 : 0,
      min_changes: min === '' ? 1 : Number(min),
    })
      .then((result) => {
        setBusy(false)
        toast(result.message, 'CI rules', result.status === 'OK' ? 'success' : 'error')
        if (result.status !== 'OK') return
        onSaved()
        onClose()
      })
      .catch(() => {
        setBusy(false)
        toast('That could not be saved.', 'CI rules', 'error')
      })
  }

  return (
    <Modal
      id="ciRulesModal"
      open
      large
      onClose={onClose}
      title={
        <>
          <i className="fas fa-envelope"></i> {subscriber === null ? 'Add an alarm' : 'Edit this alarm'}
        </>
      }
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="ciRulesForm" className="btn btn-primary" disabled={busy}>
            <i className="fas fa-check"></i> Save
          </button>
        </>
      }
    >
      <form id="ciRulesForm" onSubmit={submit}>
        <FormRow>
          <HalfField label="Emails" htmlFor="subAddress" required>
            <input
              type="email"
              className="form-control"
              id="subAddress"
              maxLength={190}
              required
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          </HalfField>
          <HalfField
            label="Customer"
            htmlFor="subCompany"
            /* Gol e fiecare client al furnizorului, ce vrea o echipa interna de
               operatiuni. Un contact al clientului isi primeste firma lui si nu
               poate primi nimic din afara ei - notificatorul verifica singur
               scopul, nu se increde in ce s-a bifat. */
            help="Empty is every customer this provider serves."
          >
            <select
              className="form-control"
              id="subCompany"
              value={company}
              onChange={(event) => setCompany(Number(event.target.value))}
            >
              <option value={0}>Every customer</option>
              {data.companies.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </HalfField>
        </FormRow>

        <hr />

        <label className="wbTilesLabel" style={{ display: 'block', marginBottom: 8 }}>
          Alarm on
        </label>

        {/*
          * Bifa e cea a portalului, scrisa de mana, NU `FormGrid.CheckBox`:
          * bucata aia isi poarta propria `<label>`, iar aici toata propozitia e
          * deja o eticheta (`.adm-policy`) - iar etichetele nu se pot cuibari
          * una in alta. Markupul e chiar cel din `subscriber_form.php`.
          */}
        <label className="adm-policy">
          <input type="checkbox" name="on_appeared" checked={appeared} onChange={(event) => setAppeared(event.target.checked)} />{' '}
          {badge('on_appeared')} <strong>A connection appeared.</strong> Something now depends on something else that did not
          before &mdash; a new application, a server moved, or a dependency nobody had recorded.
        </label>

        <label className="adm-policy">
          <input
            type="checkbox"
            name="on_disappeared"
            checked={disappeared}
            onChange={(event) => setDisappeared(event.target.checked)}
          />{' '}
          {badge('on_disappeared')} <strong>A connection stopped.</strong> Often the more interesting of the two: a service that
          no longer talks to its database has either been decommissioned or is broken, and nothing else on the platform would say
          so.
        </label>

        <label className="adm-policy">
          <input type="checkbox" name="on_surge" checked={surge} onChange={(event) => setSurge(event.target.checked)} />{' '}
          {badge('on_surge')} <strong>A sudden burst.</strong> Far more connections in one hour than usual. Nearly always
          discovery&rsquo;s own problem rather than the estate&rsquo;s, so this is off unless you run discovery.
        </label>

        <hr />

        <FormRow>
          <HalfField
            label="Send only from"
            htmlFor="subMin"
            span={6}
            help="One means every change. A higher floor is for somebody who wants to hear only when something substantial moved."
          >
            <div className="input-group">
              <input
                type="number"
                className="form-control"
                id="subMin"
                min={1}
                max={500}
                value={min}
                onChange={(event) => setMin(event.target.value)}
              />
              <span className="input-group-addon">changes</span>
            </div>
          </HalfField>
        </FormRow>
      </form>
    </Modal>
  )
}
